import {
  ChangeDetectionStrategy,
  Component, forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import {ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR} from "@angular/forms";
import {Subject} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {isObject} from "lodash";

export const EXE_CHECK_GROUP_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CheckBoxGroupComponent),
  multi: true
};

@Component({
  selector: 'jd-check-box-group',
  template: `
    <form [formGroup]="checkFormGroup">
      <ng-container *ngFor="let v of options">
        <mat-checkbox [disabled]="v.disabled"
                      [formControlName]="v.value">{{v.label}}</mat-checkbox>
      </ng-container>
    </form>
  `,
  styleUrls: ['./check-box-group.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [EXE_CHECK_GROUP_VALUE_ACCESSOR],
  host: {
    '[class.jd-check-group]': 'true',
  },
})
export class CheckBoxGroupComponent implements OnInit, ControlValueAccessor, OnDestroy {
  private _onChangeFn: Function;
  private _onTouchedFn: Function;

  private _disable: boolean;

  @Input() set disabled(value: boolean) {
    this._disable = value;
    if (value) {
      this.options.forEach(value1 => value1.disabled = true);
    }
  }

  get disabled() {
    return this._disable;
  }

  checkFormGroup: FormGroup;

  @HostBinding('class.jd-vertical-check-group')
  @Input() isVertical: boolean = false;
  private _unsubscribe$ = new Subject();
  @Input() options: Array<{ label: string, value: any, disabled: boolean }> = [];

  constructor(private fb: FormBuilder) {
    this.checkFormGroup = this.fb.group({});
  }

  private _resolveValue(obj: object) {
    const _data = [];
    if (isObject(obj)) {
      for (const objKey in obj) {
        if (obj[objKey]) {
          _data.push(objKey);
        }
      }
    }
    return _data;
  }

  private _setValue(obj: any[]) {
    if (!obj) return;
    const _data: any = {};
    obj.forEach(value => _data[value] = true);
    if (this.checkFormGroup) {
      this.checkFormGroup.setValue(_data);
    }
  }

  ngOnInit() {
    this.options.forEach(value => {
      this.checkFormGroup.addControl(value.value, new FormControl());
    });

    this.checkFormGroup
      .valueChanges
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(value => {
        if (this._onChangeFn) {
          this._onChangeFn(this._resolveValue(value));
        }
      })
  }

  registerOnChange(fn: Function): void {
    this._onChangeFn = fn;
  }

  registerOnTouched(fn: Function): void {
    this._onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disable = isDisabled;
  }

  writeValue(obj: any): void {
    this._setValue(obj);
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

}
